// src/components/admin/ContasCupons/MyCouponsTab.tsx
// Cupons do Admin (sem comissão) - IDENTIDADE GRAIA

import React, { useState } from 'react';
import {
    Ticket,
    Link as LinkIcon,
    Plus,
    Copy,
    Eye,
    EyeOff,
    Trash2,
    TrendingUp,
    DollarSign,
    Package,
    X,
    Zap,
    Target
} from 'lucide-react';
import './MyCouponsTab.css';
import { useMyCoupons, useMyCouponsStats, useMarketplaceProducts, useInvalidate } from '../../../hooks/useAdminQueries';

interface Coupon {
    _id: string;
    code: string;
    discountPercent: number;
    timesUsed: number;
    active: boolean;
    isLink: boolean;
    productId?: {
        _id: string;
        title: string;
        type: string;
    };
    createdAt: string;
}

interface MyCouponsTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const MyCouponsTab: React.FC<MyCouponsTabProps> = ({ onShowToast }) => {

    // ── 1. useState PRIMEIRO ──────────────────────────────
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createType, setCreateType] = useState<'coupon' | 'link'>('coupon');
    const [couponCode, setCouponCode] = useState('');
    const [couponDiscount, setCouponDiscount] = useState(10);
    const [selectedProduct, setSelectedProduct] = useState('');
    const [creating, setCreating] = useState(false);

    // ── 2. React Query hooks DEPOIS ───────────────────────
    const { data: couponsData, isLoading: loading } = useMyCoupons();
    const { data: statsData } = useMyCouponsStats();
    const { data: productsData } = useMarketplaceProducts();
    const invalidate = useInvalidate();

    // ── 3. Derivados ──────────────────────────────────────
    const coupons: Coupon[] = couponsData?.coupons ?? [];
    const stats = statsData ?? null;
    const products = productsData?.products?.filter((p: any) => p.active) ?? [];

    const handleOpenModal = (type: 'coupon' | 'link') => {
        setCreateType(type);
        setShowCreateModal(true);
        setCouponCode('');
        setCouponDiscount(10);
        setSelectedProduct('');
    };

    const handleCreate = async () => {
        if (!couponCode.trim()) {
            onShowToast('Digite um código', 'error');
            return;
        }
        if (createType === 'link' && !selectedProduct) {
            onShowToast('Selecione um produto', 'error');
            return;
        }

        try {
            setCreating(true);
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/admin/sales/my-coupons', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    code: couponCode.trim().toUpperCase(),
                    discountPercent: couponDiscount,
                    productId: createType === 'link' ? selectedProduct : null,
                    isLink: createType === 'link'
                })
            });

            const data = await response.json();

            if (response.ok) {
                onShowToast(`${createType === 'link' ? 'Link' : 'Cupom'} criado com sucesso!`, 'success');
                setShowCreateModal(false);
                invalidate.myCoupons();
            } else {
                onShowToast(data.message || 'Erro ao criar', 'error');
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao criar', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleToggle = async (couponId: string) => {
        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/my-coupons/${couponId}/toggle`,
                {
                    method: 'PATCH',
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.ok) {
                onShowToast('Status atualizado', 'success');
                invalidate.myCoupons();
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao atualizar', 'error');
        }
    };

    const handleDelete = async (couponId: string, code: string) => {
        if (!window.confirm(`Tem certeza que deseja deletar o cupom ${code}?`)) return;

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(
                `/api/admin/sales/my-coupons/${couponId}`,
                {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.ok) {
                onShowToast('Cupom deletado com sucesso', 'success');
                invalidate.myCoupons();
            }
        } catch (error) {
            console.error('Erro:', error);
            onShowToast('Erro ao deletar', 'error');
        }
    };

    const copyCouponCode = (code: string) => {
        navigator.clipboard.writeText(code);
        onShowToast('Código copiado!', 'info');
    };

    const copyLink = (code: string, productId: string) => {
        const link = `${window.location.origin}/marketplace/${productId}?coupon=${code}`;
        navigator.clipboard.writeText(link);
        onShowToast('Link copiado!', 'info');
    };

    const formatCurrency = (cents: number) => `R$ ${(cents / 100).toFixed(2)}`;

    if (loading) {
        return (
            <div className="my-cpn-loading">
                <div className="spinner"></div>
                <p>Carregando cupons...</p>
            </div>
        );
    }

    return (
        <div className="my-cpn-container">
            <div className="my-cpn-header">
                <div>
                    <h2>Meus Cupons</h2>
                    <p className="my-cpn-subtitle">
                        Crie cupons e links de venda sem comissão (todo o dinheiro é seu)
                    </p>
                </div>
                <div className="my-cpn-create-buttons">
                    <button className="my-cpn-btn-create-coupon" onClick={() => handleOpenModal('coupon')}>
                        <Ticket size={18} />
                        Criar Cupom
                    </button>
                    <button className="my-cpn-btn-create-link" onClick={() => handleOpenModal('link')}>
                        <LinkIcon size={18} />
                        Criar Link
                    </button>
                </div>
            </div>

            {/* Cards de Estatísticas */}
            {stats && (
                <div className="my-cpn-stats">
                    <div className="my-cpn-stat-card">
                        <div className="my-cpn-stat-icon" style={{ background: '#3b82f6' }}>
                            <Ticket size={24} />
                        </div>
                        <div className="my-cpn-stat-content">
                            <span className="my-cpn-stat-label">Total de Cupons</span>
                            <span className="my-cpn-stat-value">{stats.totalCoupons}</span>
                            <span className="my-cpn-stat-detail">{stats.activeCoupons} ativos</span>
                        </div>
                    </div>

                    <div className="my-cpn-stat-card">
                        <div className="my-cpn-stat-icon" style={{ background: '#10b981' }}>
                            <TrendingUp size={24} />
                        </div>
                        <div className="my-cpn-stat-content">
                            <span className="my-cpn-stat-label">Total de Usos</span>
                            <span className="my-cpn-stat-value">{stats.totalUses}</span>
                            <span className="my-cpn-stat-detail">{stats.totalSales} vendas</span>
                        </div>
                    </div>

                    <div className="my-cpn-stat-card highlight">
                        <div className="my-cpn-stat-icon" style={{ background: '#003BD1' }}>
                            <DollarSign size={24} />
                        </div>
                        <div className="my-cpn-stat-content">
                            <span className="my-cpn-stat-label">Receita Gerada</span>
                            <span className="my-cpn-stat-value">{formatCurrency(stats.revenueFromCoupons)}</span>
                            <span className="my-cpn-stat-detail">100% seu (sem comissão)</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Lista de Cupons */}
            {coupons.length === 0 ? (
                <div className="my-cpn-empty">
                    <Target size={64} strokeWidth={1.5} />
                    <h3>Nenhum cupom criado ainda</h3>
                    <p>Crie cupons genéricos ou links específicos para seus produtos</p>
                    <button className="my-cpn-btn-primary" onClick={() => handleOpenModal('coupon')}>
                        <Plus size={18} />
                        Criar Primeiro Cupom
                    </button>
                </div>
            ) : (
                <div className="my-cpn-grid">
                    {coupons.map((coupon: Coupon) => (
                        <div key={coupon._id} className={`my-cpn-card ${!coupon.active ? 'inactive' : ''}`}>
                            <div className="my-cpn-card-header">
                                <div className="my-cpn-code-container">
                                    <span className="my-cpn-code">{coupon.code}</span>
                                    {coupon.isLink ? (
                                        <span className="my-cpn-badge-link">Link</span>
                                    ) : (
                                        <span className="my-cpn-badge-coupon">Cupom</span>
                                    )}
                                </div>
                                <span className={`my-cpn-status ${coupon.active ? 'active' : 'inactive'}`}>
                                    {coupon.active ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>

                            <div className="my-cpn-details">
                                <div className="my-cpn-discount">
                                    <Zap size={14} />
                                    {coupon.discountPercent === 0 ? 'Sem desconto' : `${coupon.discountPercent}% de desconto`}
                                </div>
                                {coupon.productId && (
                                    <div className="my-cpn-product">
                                        <Package size={14} />
                                        {coupon.productId.title}
                                    </div>
                                )}
                                <div className="my-cpn-usage">
                                    Usado <strong>{coupon.timesUsed}</strong>{' '}
                                    {coupon.timesUsed === 1 ? 'vez' : 'vezes'}
                                </div>
                            </div>

                            <div className="my-cpn-actions">
                                {coupon.isLink && coupon.productId ? (
                                    <button className="my-cpn-btn-copy-link" onClick={() => copyLink(coupon.code, coupon.productId!._id)} title="Copiar link">
                                        <LinkIcon size={16} />
                                        Copiar Link
                                    </button>
                                ) : (
                                    <button className="my-cpn-btn-copy-code" onClick={() => copyCouponCode(coupon.code)} title="Copiar código">
                                        <Copy size={16} />
                                        Copiar
                                    </button>
                                )}
                                <button className="my-cpn-btn-toggle" onClick={() => handleToggle(coupon._id)} title={coupon.active ? 'Desativar' : 'Ativar'}>
                                    {coupon.active ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                                <button className="my-cpn-btn-delete" onClick={() => handleDelete(coupon._id, coupon.code)} title="Deletar">
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de Criação */}
            {showCreateModal && (
                <div className="my-cpn-modal-overlay" onClick={() => !creating && setShowCreateModal(false)}>
                    <div className="my-cpn-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="my-cpn-modal-header">
                            <h3>{createType === 'link' ? 'Criar Link de Venda' : 'Criar Cupom Genérico'}</h3>
                            <button className="my-cpn-btn-close" onClick={() => setShowCreateModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="my-cpn-modal-body">
                            <div className="my-cpn-admin-notice">
                                <DollarSign size={16} />
                                <strong>Admin sem comissão:</strong> Todo o dinheiro desta venda será seu
                            </div>

                            <div className="my-cpn-form-group">
                                <label>Código</label>
                                <input
                                    type="text"
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    placeholder="EXEMPLO10"
                                    maxLength={20}
                                    disabled={creating}
                                    autoFocus
                                />
                            </div>

                            <div className="my-cpn-form-group">
                                <label>Desconto (%)</label>
                                <input
                                    type="number"
                                    value={couponDiscount}
                                    onChange={(e) => setCouponDiscount(Math.min(100, Math.max(0, Number(e.target.value))))}
                                    min="0"
                                    max="100"
                                    disabled={creating}
                                />
                                <small>
                                    {createType === 'link' && couponDiscount === 0
                                        ? 'Link sem desconto (preço cheio)'
                                        : 'Desconto não afeta sua receita'}
                                </small>
                            </div>

                            {createType === 'link' && (
                                <div className="my-cpn-form-group">
                                    <label>Produto</label>
                                    <select value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)} disabled={creating}>
                                        <option value="">Selecione um produto</option>
                                        {products.map((product: any) => (
                                            <option key={product._id} value={product._id}>
                                                {product.title}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="my-cpn-modal-footer">
                            <button onClick={() => setShowCreateModal(false)} disabled={creating}>Cancelar</button>
                            <button className="my-cpn-btn-confirm" onClick={handleCreate} disabled={creating}>
                                {creating ? 'Criando...' : 'Criar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyCouponsTab;
